//	///////////////////////
//	MARK: ENDPOINTS
const BASE = '/api/compliance/documentation'

export const AUTHORITY_ENDPOINTS = {
  supervisoryAuthority: `${BASE}/supervisoryAuthority`,
  supervisoryAuthorityContact: `${BASE}/supervisoryAuthorityContact`,
  authorityMailbox: `${BASE}/authorityMailbox`,
  authorityMailboxThreads: `${BASE}/authorityMailbox/threads`,
  authorityMailboxSent: `${BASE}/authorityMailbox/sent`,
  authorityMailboxSend: `${BASE}/authorityMailbox/send`,
  authorityMailboxUnreadCount: `${BASE}/authorityMailbox/unreadCount`,
  authorityMailboxFolders: `${BASE}/authorityMailbox/folders`,
  authorityMailboxMessages: `${BASE}/authorityMailbox/messages`,
  authorityMailboxRecipientOptions: `${BASE}/authorityMailbox/recipientOptions`,
  submission: `${BASE}/submission`,
  submissionOverview: `${BASE}/submission/overview`,
  submissionDashboard: `${BASE}/submission/dashboard`,
  submissionHistoryImport: `${BASE}/submission/historyImport`,
  checklist: `${BASE}/complianceChecklist`,
  checklistImport: `${BASE}/complianceChecklist/import`,
  checklistImportAnchors: `${BASE}/complianceChecklist/importAnchors`,
  checklistGenerate: `${BASE}/complianceChecklist/generate`,
  checklistPreview: `${BASE}/complianceChecklist/preview`,
  checklistHeadings: `${BASE}/complianceChecklist/headings`,
  checklistAnchor: `${BASE}/complianceChecklist/anchor`,
} as const

//	///////////////////////
//	MARK: ENUMS
export enum EDocumentApprovalRegime {
  PRIOR_APPROVAL = 'PRIOR_APPROVAL',
  INDIRECT_APPROVAL = 'INDIRECT_APPROVAL',
  NOT_REQUIRED = 'NOT_REQUIRED',
}

export enum ESubmissionStatus {
  PREPARING = 'PREPARING',
  SUBMITTED = 'SUBMITTED',
  UNDER_ASSESSMENT = 'UNDER_ASSESSMENT',
  FINDINGS_RECEIVED = 'FINDINGS_RECEIVED',
  APPROVED = 'APPROVED',
  CONSOLIDATED_SENT = 'CONSOLIDATED_SENT',
  CLOSED_NOT_ACCEPTED = 'CLOSED_NOT_ACCEPTED',
  WITHDRAWN = 'WITHDRAWN',
}

// Miroir client de DocumentSubmissionService.ALLOWED — le serveur reste l'arbitre, ceci ne
// sert qu'à ne proposer que des transitions qui ont une chance d'aboutir.
export const SUBMISSION_TRANSITIONS: Record<ESubmissionStatus, ESubmissionStatus[]> = {
  [ESubmissionStatus.PREPARING]: [ESubmissionStatus.SUBMITTED, ESubmissionStatus.WITHDRAWN],
  [ESubmissionStatus.SUBMITTED]: [
    ESubmissionStatus.UNDER_ASSESSMENT,
    ESubmissionStatus.FINDINGS_RECEIVED,
    ESubmissionStatus.APPROVED,
    ESubmissionStatus.CLOSED_NOT_ACCEPTED,
    ESubmissionStatus.WITHDRAWN,
  ],
  [ESubmissionStatus.UNDER_ASSESSMENT]: [
    ESubmissionStatus.FINDINGS_RECEIVED,
    ESubmissionStatus.APPROVED,
    ESubmissionStatus.CLOSED_NOT_ACCEPTED,
    ESubmissionStatus.WITHDRAWN,
  ],
  [ESubmissionStatus.FINDINGS_RECEIVED]: [
    ESubmissionStatus.SUBMITTED,
    ESubmissionStatus.CLOSED_NOT_ACCEPTED,
    ESubmissionStatus.WITHDRAWN,
  ],
  [ESubmissionStatus.APPROVED]: [ESubmissionStatus.CONSOLIDATED_SENT],
  [ESubmissionStatus.CONSOLIDATED_SENT]: [],
  [ESubmissionStatus.CLOSED_NOT_ACCEPTED]: [],
  [ESubmissionStatus.WITHDRAWN]: [],
}

//	///////////////////////
//	MARK: TYPES
export interface ISupervisoryAuthority {
  clazz: string
  id: number
  officialName: string
  shortName: string | null
  countryId: number | null
  countryName: string | null
  contactName: string | null
  contactEmail: string | null
  postalAddress: string | null
  procedureReference: string | null
  emailSubjectTemplate: string | null
  manualFileNameTemplate: string | null
  checklistFileNameTemplate: string | null
  mailboxAccountId: number | null
  mailboxAccountLabel: string | null
  active: boolean
}

export interface ISupervisoryAuthorityContact {
  clazz: string
  id: number
  authorityId: number
  authorityShortName: string | null
  authorityOfficialName: string | null
  countryId: number | null
  fullName: string
  role: string | null
  email: string | null
  phone: string | null
  notes: string | null
  active: boolean
}

export interface ISubmission {
  clazz: string
  id: number
  revisionId: number | null
  revisionName: string | null
  revisionDate: string | null
  documentId: number | null
  complianceChecklist: { id: number; code: string; name: string | null } | null
  documentTitle: string | null
  authorityId: number | null
  authorityName: string | null
  status: ESubmissionStatus
  attemptNumber: number
  submittedAt: string | null
  decisionAt: string | null
  authorityReference: string | null
  emailSubject: string | null
  manualFileName: string | null
  notes: string | null
  closureReason: string | null
  transmittalFileId: number | null
  checklistSentFileId: number | null
  signedChecklistFileId: number | null
  manualFileId: number | null
  transmittalFileKey: string | null
  checklistSentFileKey: string | null
  signedChecklistFileKey: string | null
  manualFileKey: string | null
  conversationThreadId: number | null
  conversationId: string | null
  submittedById: number | null
  submittedByName: string | null
}

// GET submission/overview — tableau nu, une ligne par document soumis à une autorité.
export interface ISubmissionOverviewRow {
  documentId: number
  title: string
  shortCode: string | null
  documentTypeName: string | null
  approvalRegime: EDocumentApprovalRegime | null
  authorityId: number | null
  authorityName: string | null
  approvedRevision: {
    revisionId: number
    name: string | null
    decisionAt: string | null
    authorityReference: string | null
  } | null
  draftRevision: { revisionId: number; name: string | null } | null
  pendingSubmission: {
    submissionId: number
    revisionId: number
    revisionName: string | null
    status: ESubmissionStatus
    attemptNumber: number
    submittedAt: string | null
    daysWaiting: number | null
  } | null
  actionNeeded: boolean
}

export interface IDashboardFile {
  id: number
  fileName: string | null
  mimeType: string | null
  awsS3KeyString: string | null
}

export interface IDashboardRevisionCard {
  revisionId: number
  name: string | null
  issueLabel: string | null
  status: string | null
  revisionType: string | null
  dateCreated: string | null
  expectedPublishDate: string | null
  publishedAt: string | null
  publishedByName: string | null
  revisionReason: string | null
  publishNotes: string | null
  signedApprovalFile: IDashboardFile | null
  transmittalLetterFile: IDashboardFile | null
  referenceDocuments: IDashboardFile[]
}

export interface IDashboardSubmission {
  id: number
  revisionId: number
  revisionName: string | null
  attemptNumber: number
  status: ESubmissionStatus
  submittedAt: string | null
  decisionAt: string | null
  authorityReference: string | null
  notes: string | null
  closureReason: string | null
  conversationThreadId: number | null
  transmittalFile: IDashboardFile | null
  manualFile: IDashboardFile | null
  signedChecklistFile: IDashboardFile | null
}

// Une ligne du tableau de bord des révisions : tous les manuels vivants, dossier et fil des
// soumissions compris.
export interface IRevisionDashboardRow {
  documentId: number
  title: string
  shortCode: string | null
  documentTypeName: string | null
  approvalRegime: EDocumentApprovalRegime | null
  authorityId: number | null
  authorityName: string | null
  revisionCount: number
  publishedRevision: IDashboardRevisionCard | null
  draftRevision: IDashboardRevisionCard | null
  lastDecision: {
    submissionId: number
    revisionId: number
    revisionName: string | null
    status: ESubmissionStatus
    decisionAt: string | null
    authorityReference: string | null
  } | null
  pendingSubmission: ISubmissionOverviewRow['pendingSubmission']
  submissions: IDashboardSubmission[]
  actionNeeded: boolean
}

export interface ISubmissionPreflight {
  blocking: string[]
  warnings: string[]
  canSend: boolean
}

export interface ITransmittalLetter {
  html: string
  changes: { chapter: string | null; title: string | null; pageNumber: string | null }[]
  problems: string[]
}

export interface IChangeNoteRow {
  itemId: number
  subItemNumber: string | null
  subject: string | null
  description: string | null
  location: string | null
  onChangedPage: boolean
  text: string | null
}

export interface IChecklistRow {
  clazz: string
  id: number
  code: string
  name: string | null
  reference: string | null
  publishedOn: string | null
  active: boolean
  authorityId: number | null
  authorityName: string | null
  itemCount: number
}

export interface IChecklistPreviewRow {
  itemId: number
  itemNumber: string | null
  subject: string | null
  subItemNumber: string | null
  description: string | null
  location: string | null
  applicable: boolean | null
  remark: string | null
  anchored: boolean
  onChangedPage: boolean
  headingId: number | null
  headingChapter: string | null
  headingTitle: string | null
  headingPage: string | null
  subHeadingId: number | null
  subHeadingChapter: string | null
  freeText: string | null
  applicableOwn: boolean | null
  notApplicableReason: string | null
}

export interface IChecklistPreview {
  fileName: string | null
  mimeType: string | null
  format: string | null
  itemCount: number
  answered: number
  unanchored: string[]
  onChangedPages: string[]
  rows: IChecklistPreviewRow[]
}

export interface IChecklistHeading {
  id: number
  chapter: string | null
  title: string | null
  page: string | null
  level: number | null
}

export interface IAnchorRow {
  itemId: number
  subItemNumber: string | null
  location: string | null
  anchored: boolean
  headingId: number | null
  headingChapter: string | null
  headingTitle: string | null
  headingPage: string | null
  subHeadingId: number | null
  subHeadingChapter: string | null
  freeText: string | null
  applicableOwn: boolean | null
  notApplicableReason: string | null
}

export interface IChecklistImportResult {
  imported: number
  warning?: string
  error?: string
  checklistId?: number
  format?: string
}

export interface IAnchorImportReport {
  applied: boolean
  format: string | null
  rowsRead: number
  anchored: number
  freeText: number
  unresolved: { item: string; location: string | null; reason: string }[]
  unknownItems: string[]
}

export interface IHistoryImportLine {
  sheet: string
  revision: string | null
  created: boolean
  dryRun?: boolean
  submissionId?: number
  reason?: string
}

// ── Boîte Autorité (fils du canal mail AUTHORITY) ──
export interface IAuthorityMailboxInfo {
  id: number
  label: string
  enabled: boolean
  fromAddress: string | null
  // Signature HTML de la boîte — ajoutée côté serveur à chaque sortant du canal.
  signatureHtml: string | null
  lastPolledAt: string | null
  lastError: string | null
}

export interface IAuthorityThreadSubmission {
  id: number
  status: ESubmissionStatus
  documentTitle: string | null
  revisionName: string | null
}

export interface IAuthorityThreadSummary {
  id: number
  subject: string | null
  status: string | null
  correspondent: string | null
  lastActivityAt: string | null
  messageCount: number
  unreadCount: number
  lastMessage: { direction: string | null; excerpt: string | null; at: string | null } | null
  submission: IAuthorityThreadSubmission | null
  // Dossier de classement — null / absent : « non classés ».
  folderId: number | null
}

// Arbre libre des dossiers de la Boîte Autorité — liste plate, le client reconstruit l'arbre.
export interface IAuthorityMailFolder {
  id: number
  name: string
  parentId: number | null
  sortOrder: number
}

export interface IAuthorityMailboxOverview {
  mailboxes: IAuthorityMailboxInfo[]
  folders: IAuthorityMailFolder[]
  threads: IAuthorityThreadSummary[]
}

// GET authorityMailbox/recipientOptions — suggestions du composeur (autorités + annuaire).
export interface IAuthorityRecipientOption {
  label: string
  email: string
  kind: 'AUTHORITY' | 'PERSON'
}

// GET authorityMailbox/unreadCount — la pastille : entrants non lus du canal AUTHORITY.
export interface IAuthorityUnreadCount {
  count: number
}

export interface IAuthorityMessageAttachment {
  id: number
  fileId: number | null
  fileKey?: string | null
  fileName: string | null
  mimeType: string | null
  fileSize: number | null
  // Masquée du panneau latéral (tri partagé, réversible) — absente = visible.
  hidden?: boolean
}

// Suivi d'ouverture par pixel — indicatif : un client qui bloque les images ne compte pas.
export interface IAuthorityMessageTracking {
  firstOpenedAt: string | number | null
  lastOpenedAt: string | number | null
  openCount: number
}

export interface IAuthorityThreadMessage {
  id: number
  direction: 'INBOUND' | 'OUTBOUND'
  fromAddress: string | null
  fromAlias: string | null
  toAddress: string | null
  // CSV — le CCI n'est jamais exposé dans le fil, seul le CC est visible.
  ccAddresses: string | null
  subject: string | null
  body: string | null
  htmlBody: string | null
  // Render as JSON : tantôt ISO, tantôt epoch — ne jamais supposer une string.
  sentAt: string | number | null
  receivedAt: string | number | null
  tracking: IAuthorityMessageTracking | null
  attachments: IAuthorityMessageAttachment[]
}

// GET authorityMailbox/sent — tous les OUTBOUND du canal AUTHORITY, D8 compris.
export interface IAuthoritySentMessage {
  id: number
  threadId: number | null
  threadSubject: string | null
  subject: string | null
  toAddress: string | null
  ccAddresses: string | null
  status: string | null
  sentAt: string | number | null
  tracking: IAuthorityMessageTracking | null
  attachmentCount: number
  submission: IAuthorityThreadSubmission | null
}

export interface IAuthoritySentMessages {
  messages: IAuthoritySentMessage[]
}

export interface IAuthorityThreadDetail {
  id: number
  subject: string | null
  status: string | null
  lastActivityAt: string | null
  submission: IAuthorityThreadSubmission | null
  messages: IAuthorityThreadMessage[]
}

// Intervenant d'un fil : détecté depuis le trafic ou ajouté à la main ; le détail (nom,
// téléphone) vient de l'annuaire quand l'adresse y correspond.
export interface IAuthorityThreadParticipant {
  id: number
  email: string
  role: 'TO' | 'CC'
  manuallyAdded: boolean
  firstName: string | null
  lastName: string | null
  displayName: string | null
  phone: string | null
  kind: 'AUTHORITY' | 'PERSON' | 'MANUAL' | 'DETECTED'
}

export interface IHistoryImportResult {
  applied: boolean
  created: IHistoryImportLine[]
  createdCount: number
  skipped: IHistoryImportLine[]
  skippedCount: number
  unmatchedSheets?: string[]
}
